import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Church, Users, Building2, Download, Trash2 } from 'lucide-react';
import AdminLayout from '../components/admin/AdminLayout';
import { getDocumentStats, listDocuments, getSignedUrl, deleteDocument } from '../lib/documents';
import { useAuth } from '../context/AuthContext';

const STAT_CARDS = [
  { key: 'total', label: 'Documents', icon: FileText },
  { key: 'Church Report', label: 'Church Reports', icon: Church },
  { key: 'Ministry Report', label: 'Ministry Reports', icon: Users },
  { key: 'District Report', label: 'District Reports', icon: Building2 },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  async function loadData() {
    setLoading(true);
    setError('');
    try {
      const [statData, recentDocs] = await Promise.all([
        getDocumentStats(),
        listDocuments({ limit: 5 }),
      ]);
      setStats(statData);
      setRecent(recentDocs);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  async function handleView(doc) {
    try {
      const url = await getSignedUrl(doc.file_path);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDelete(doc) {
    if (!window.confirm(`Permanently delete "${doc.title}"? This cannot be undone.`)) return;
    try {
      await deleteDocument(doc);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="text-[22px] font-semibold text-[#00384D] tracking-tight">
          Welcome, {user?.email?.split('@')[0] || 'Admin'}
        </h1>
        <p className="text-slate-500 text-sm mt-1">Here's what's happening across the district.</p>
      </div>

      {error && (
        <div className="mb-6 rounded-md border border-[#C21E26]/20 bg-[#C21E26]/5 px-4 py-3 text-sm text-[#C21E26]">
          {error}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {STAT_CARDS.map(({ key, label, icon: Icon }) => (
          <div
            key={key}
            className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm h-[104px] flex flex-col justify-between"
          >
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                {label}
              </span>
              <div className="h-7 w-7 rounded-md bg-[#4EA6B3]/10 flex items-center justify-center shrink-0">
                <Icon className="h-3.5 w-3.5 text-[#007A96]" strokeWidth={2} />
              </div>
            </div>
            <p className="text-[28px] leading-none font-semibold text-[#00384D]">
              {loading ? '—' : stats?.[key] ?? 0}
            </p>
          </div>
        ))}
      </div>

      {/* Recent documents */}
      <div className="bg-white rounded-xl border border-slate-200 mt-6 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="font-semibold text-[#00384D] text-[15px]">Recent Documents</h2>
          <Link
            to="/admin/documents"
            className="text-sm text-[#007A96] hover:text-[#00384D] font-medium transition-colors"
          >
            View all →
          </Link>
        </div>

        {loading ? (
          <p className="px-6 py-10 text-sm text-slate-400 text-center">Loading…</p>
        ) : recent.length === 0 ? (
          <div className="px-6 py-14 text-center">
            <div className="mx-auto h-11 w-11 rounded-full bg-[#00384D]/5 flex items-center justify-center mb-4">
              <FileText className="h-5 w-5 text-[#4EA6B3]" strokeWidth={1.75} />
            </div>
            <p className="text-[#00384D] font-medium text-sm">No documents yet</p>
            <p className="text-slate-400 text-sm mt-1">
              Uploaded district and church reports will appear here.
            </p>
            <Link
              to="/admin/documents"
              className="inline-block mt-4 bg-[#00384D] text-white text-sm font-medium rounded-md px-4 py-2 hover:bg-[#00495F] transition-colors"
            >
              Upload Document
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {recent.map((doc) => (
              <li key={doc.id} className="px-6 py-3.5 flex items-center justify-between gap-4 hover:bg-slate-50/80 transition-colors">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#00384D] truncate">{doc.title}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {doc.category} · {doc.file_type?.toUpperCase()} ·{' '}
                    {new Date(doc.created_at).toLocaleDateString('en-ZA', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => handleView(doc)}
                    className="text-slate-400 hover:text-[#007A96] hover:bg-[#007A96]/8 rounded-md p-1.5 transition-colors"
                    aria-label="View document"
                  >
                    <Download className="h-4 w-4" strokeWidth={1.75} />
                  </button>
                  <button
                    onClick={() => handleDelete(doc)}
                    className="text-slate-400 hover:text-[#C21E26] hover:bg-[#C21E26]/8 rounded-md p-1.5 transition-colors"
                    aria-label="Delete document"
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AdminLayout>
  );
}
